import { memo, useEffect, useMemo, useState } from 'react';

interface AchievementNotificationProps {
  show: boolean;
  title: string;
  description: string;
  points: number;
  icon?: string;
  onClose: () => void;
  metadata?: {
    subtitle?: string;
    [key: string]: any;
  };
}

const AchievementNotificationComponent = ({
  show,
  title,
  description,
  points,
  icon = 'ri-trophy-line',
  onClose,
  metadata
}: AchievementNotificationProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(show);
  }, [show]);

  const confetti = useMemo(() => {
    if (!show) {
      return [];
    }

    return Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 2 + Math.random() * 1
    }));
  }, [show]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!show) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Modal de Conquista */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className={`bg-gradient-to-br from-yellow-50 to-orange-50 rounded-3xl shadow-2xl p-8 max-w-md w-full pointer-events-auto transform transition-all duration-500 relative ${
            isVisible ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
          }`}
        >
          {/* Botão Fechar */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white hover:shadow-lg transition-all z-10"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-gray-700 text-xl"></i>
          </button>

          {/* Ícone de Conquista */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              {/* Círculo de fundo animado */}
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-ping opacity-75"></div>
              
              {/* Ícone principal */}
              <div className="relative w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-xl">
                <i className={`${icon} text-white text-5xl`}></i>
              </div>
            </div>
          </div>

          {/* Título */}
          <h2 className="text-3xl font-bold text-center mb-4 text-gray-800">
            🎉 Conquista Desbloqueada!
          </h2>

          {/* Subtítulo (se existir) */}
          {metadata?.subtitle && (
            <p className="text-center text-gray-600 mb-3 text-base">
              {metadata.subtitle}
            </p>
          )}

          {/* Nome da Conquista (destaque) */}
          <h3 className="text-2xl font-bold text-center mb-2 text-orange-600">
            {title}
          </h3>
          
          {/* Descrição (título do nível) */}
          <p className="text-center text-gray-700 font-medium mb-6 text-lg">
            {description}
          </p>

          {/* Pontos Ganhos */}
          <div className="bg-white rounded-2xl p-4 border-2 border-orange-200 mb-6">
            <div className="flex items-center justify-center gap-3">
              <i className="ri-star-fill text-yellow-500 text-2xl"></i>
              <span className="text-3xl font-bold text-orange-600">+{points}</span>
              <span className="text-gray-600 font-medium">pontos</span>
            </div>
          </div>

          {/* Botão de Ação */}
          <button
            onClick={handleClose}
            className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold py-4 rounded-2xl hover:from-yellow-500 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Continuar
          </button>
        </div>
      </div>

      {/* Confetes */}
      <div className="fixed inset-0 z-40 pointer-events-none overflow-hidden">
        {confetti.map((item) => (
          <div
            key={item.id}
            className="absolute top-0 w-2 h-2 rounded-full animate-fall"
            style={{
              left: `${item.left}%`,
              backgroundColor: ['#fbbf24', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6'][Math.floor(Math.random() * 5)],
              animationDelay: `${item.delay}s`,
              animationDuration: `${item.duration}s`
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes fall {
          0% {
            transform: translateY(-10px) rotate(0deg);
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }

        .animate-fall {
          animation: fall linear forwards;
        }
      `}</style>
    </>
  );
}

const AchievementNotification = memo(AchievementNotificationComponent);

export default AchievementNotification;
